import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { AlertTriangle, CheckCircle, Clock, XCircle } from 'lucide-react';
import api from '@/api/api';
import { toast } from 'sonner';

export default function ComplianceDashboard() {
  const [report, setReport] = useState(null);
  const [expiringDocs, setExpiringDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('summary');

  const fetchComplianceData = async () => {
    setLoading(true);
    try {
      const [reportRes, expiringRes] = await Promise.all([
        api.get('/documents/compliance-report'),
        api.get('/documents/expiring', { params: { days: 30 } }),
      ]);
      if (reportRes.data?.success) setReport(reportRes.data.report);
      if (expiringRes.data?.success) setExpiringDocs(expiringRes.data.data || []);
    } catch (error) {
      toast.error('Erro ao carregar dados de conformidade');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComplianceData();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'compliant': return 'bg-emerald-100 text-emerald-800';
      case 'warning': return 'bg-amber-100 text-amber-800';
      case 'non_compliant': return 'bg-red-100 text-red-800';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'compliant': return <CheckCircle className="w-5 h-5 text-emerald-600" />;
      case 'warning': return <Clock className="w-5 h-5 text-amber-600" />;
      case 'non_compliant': return <XCircle className="w-5 h-5 text-red-600" />;
      default: return <AlertTriangle className="w-5 h-5 text-muted-foreground" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-lg text-muted-foreground">Carregando dados de conformidade...</p>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-lg text-destructive">Erro ao carregar relatório de conformidade</p>
      </div>
    );
  }

  const tabs = ['summary', 'drivers', 'vehicles', 'expiring'];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold tracking-tight">Dashboard de Conformidade Documental</h1>
        <Button onClick={fetchComplianceData}>Atualizar</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Motoristas Conformes</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {report.summary.drivers.compliant}/{report.summary.drivers.total}
                </p>
              </div>
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Veículos Conformes</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {report.summary.vehicles.compliant}/{report.summary.vehicles.total}
                </p>
              </div>
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Problemas Críticos</p>
                <p className="text-2xl font-bold text-red-600">
                  {report.summary.critical_issues.length}
                </p>
              </div>
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avisos</p>
                <p className="text-2xl font-bold text-amber-600">
                  {report.summary.warnings.length}
                </p>
              </div>
              <AlertTriangle className="w-10 h-10 text-amber-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="border-b">
        <nav className="-mb-px flex space-x-2">
          {tabs.map((tab) => (
            <Button
              key={tab}
              variant={selectedTab === tab ? 'default' : 'ghost'}
              onClick={() => setSelectedTab(tab)}
            >
              {tab === 'summary' && 'Resumo'}
              {tab === 'drivers' && 'Motoristas'}
              {tab === 'vehicles' && 'Veículos'}
              {tab === 'expiring' && 'A Expirar'}
            </Button>
          ))}
        </nav>
      </div>

      {selectedTab === 'summary' && (
        <div className="space-y-6">
          {report.summary.critical_issues.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-red-600 flex items-center gap-2">
                  <XCircle className="w-5 h-5" />
                  Problemas Críticos ({report.summary.critical_issues.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {report.summary.critical_issues.map((issue, idx) => (
                    <div key={idx} className="p-3 bg-red-50 rounded-lg border border-red-200">
                      <div className="font-medium">
                        {issue.type === 'driver' ? '👤' : '🚗'} {issue.name}
                      </div>
                      <ul className="mt-2 space-y-1 text-sm">
                        {issue.issues.map((doc, docIdx) => (
                          <li key={docIdx} className="text-red-700">
                            • {doc.name}
                            {doc.days_expired && ` (Expirado há ${doc.days_expired} dias)`}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {report.summary.warnings.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-amber-600 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Avisos ({report.summary.warnings.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {report.summary.warnings.map((warning, idx) => (
                    <div key={idx} className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="font-medium">
                        {warning.type === 'driver' ? '👤' : '🚗'} {warning.name}
                      </div>
                      <ul className="mt-2 space-y-1 text-sm">
                        {warning.issues.map((doc, docIdx) => (
                          <li key={docIdx} className="text-amber-700">
                            • {doc.name} (Expira em {doc.days_remaining} dias)
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {selectedTab === 'drivers' && (
        <Card>
          <CardHeader>
            <CardTitle>Estado dos Motoristas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase">Nome</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase">Conformidade</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase">Problemas</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {report.drivers.map((driver, idx) => (
                    <tr key={idx}>
                      <td className="px-4 py-3">{driver.driver_name}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs ${getStatusColor(driver.driver_status)}`}>
                          {driver.driver_status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(driver.status)}
                          <span className={`px-2 py-1 rounded text-xs ${getStatusColor(driver.status)}`}>
                            {driver.status}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {driver.missing_documents.length > 0 && (
                          <span className="text-red-600 text-sm">{driver.missing_documents.length} em falta</span>
                        )}
                        {driver.expired_documents.length > 0 && (
                          <span className="text-red-600 text-sm ml-2">{driver.expired_documents.length} expirados</span>
                        )}
                        {driver.expiring_soon.length > 0 && (
                          <span className="text-amber-600 text-sm ml-2">{driver.expiring_soon.length} a expirar</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {selectedTab === 'vehicles' && (
        <Card>
          <CardHeader>
            <CardTitle>Estado dos Veículos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase">Matrícula</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase">Conformidade</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase">Problemas</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {report.vehicles.map((vehicle, idx) => (
                    <tr key={idx}>
                      <td className="px-4 py-3">{vehicle.license_plate}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs ${getStatusColor(vehicle.vehicle_status)}`}>
                          {vehicle.vehicle_status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(vehicle.status)}
                          <span className={`px-2 py-1 rounded text-xs ${getStatusColor(vehicle.status)}`}>
                            {vehicle.status}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {vehicle.missing_documents.length > 0 && (
                          <span className="text-red-600 text-sm">{vehicle.missing_documents.length} em falta</span>
                        )}
                        {vehicle.expired_documents.length > 0 && (
                          <span className="text-red-600 text-sm ml-2">{vehicle.expired_documents.length} expirados</span>
                        )}
                        {vehicle.expiring_soon.length > 0 && (
                          <span className="text-amber-600 text-sm ml-2">{vehicle.expiring_soon.length} a expirar</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {selectedTab === 'expiring' && (
        <Card>
          <CardHeader>
            <CardTitle>Documentos a Expirar (Próximos 30 dias)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase">Entidade</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase">Tipo</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase">Documento</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase">Data Expiração</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground uppercase">Dias Restantes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {expiringDocs.map((doc, idx) => (
                    <tr key={idx} className={doc.days_remaining <= 7 ? 'bg-red-50' : doc.days_remaining <= 15 ? 'bg-amber-50' : ''}>
                      <td className="px-4 py-3">
                        {doc.entity_target === 'driver' ? '👤' : '🚗'} {doc.entity_name}
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-muted rounded text-xs">{doc.entity_target}</span>
                      </td>
                      <td className="px-4 py-3">{doc.type_name}</td>
                      <td className="px-4 py-3">
                        {new Date(doc.expiry_date).toLocaleDateString('pt-PT')}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          doc.days_remaining <= 7 ? 'bg-red-100 text-red-800' :
                          doc.days_remaining <= 15 ? 'bg-amber-100 text-amber-800' :
                          'bg-emerald-100 text-emerald-800'
                        }`}>
                          {doc.days_remaining} dias
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
